import { Form, Input, InputNumber, Switch, Flex, Button } from "antd";
import TextArea from "antd/es/input/TextArea";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const DEFAULT_QUESTIONS = [
    "The overall quality of the products",
    "The quality of packaging",
    "The quality of store's presentation",
    "The quality of customer service",
];

const DEFAULT_OPTIONS = ["Very Poor", "Poor", "Neutral", "Good", "Excellent"];

const getSafeCount = (value, fallback) => Math.min(Math.max(Number(value) || fallback, 1), 10);

const getItem = (items, index, defaults, prefix, withScore = false) => {
    const existing = items?.[index] || {};
    return {
        label: existing.label || defaults[index] || `${prefix} ${index + 1}`,
        value: existing.value || `${prefix.toLowerCase().replaceAll(" ", "-")}-${index + 1}`,
        ...(withScore ? { score: existing.score ?? index + 1 } : {}),
    };
};

const RatingScaleForm = ({ formValues, form, onFieldChange }) => {
    const { t } = useTranslation();
    const questionCount = getSafeCount(formValues?.questionCount, DEFAULT_QUESTIONS.length);
    const optionCount = getSafeCount(formValues?.optionCount, DEFAULT_OPTIONS.length);

    const commitFormValues = () => {
        setTimeout(() => {
            onFieldChange?.({ ...form?.getFieldsValue(true) });
        }, 0);
    };

    const syncList = (listName, countName, defaults, prefix, withScore, nextCount) => {
        const currentList = form?.getFieldValue(listName) || formValues?.[listName] || [];
        const nextList = Array.from({ length: nextCount }, (_, index) =>
            getItem(currentList, index, defaults, prefix, withScore)
        );
        form?.setFieldsValue({
            [countName]: nextCount,
            [listName]: nextList,
        });
        commitFormValues();
    };

    const addQuestion = () => {
        syncList("questions", "questionCount", DEFAULT_QUESTIONS, "Question", false, questionCount + 1);
    };

    const removeQuestion = (index) => {
        const currentQuestions = form?.getFieldValue("questions") || formValues?.questions || [];
        const nextQuestions = currentQuestions.filter((_, currentIndex) => currentIndex !== index);
        const nextCount = Math.max(questionCount - 1, 1);
        form?.setFieldsValue({
            questionCount: nextCount,
            questions: nextQuestions.length > 0 ? nextQuestions : [getItem([], 0, DEFAULT_QUESTIONS, "Question")],
        });
        commitFormValues();
    };

    const addOption = () => {
        syncList("options", "optionCount", DEFAULT_OPTIONS, "Option", true, optionCount + 1);
    };

    const removeOption = (index) => {
        const currentOptions = form?.getFieldValue("options") || formValues?.options || [];
        const nextOptions = currentOptions.filter((_, currentIndex) => currentIndex !== index);
        const nextCount = Math.max(optionCount - 1, 1);
        form?.setFieldsValue({
            optionCount: nextCount,
            options: nextOptions.length > 0 ? nextOptions : [getItem([], 0, DEFAULT_OPTIONS, "Option", true)],
        });
        commitFormValues();
    };

    return (
        <>
            <Form.Item name="label" label={t("Label")}>
                <Input placeholder={t("Enter label")} />
            </Form.Item>

            <Form.Item name="tooltip" label={t("Tooltip")} className="horizontal-form-item" valuePropName="checked">
                <Switch size="small" />
            </Form.Item>

            {formValues?.tooltip && (
                <Form.Item label={t("Help Text")} name="helpText">
                    <TextArea rows={2} placeholder={t("Enter help text")} />
                </Form.Item>
            )}

            <Form.Item name="mandatory" label={t("Mandatory")} className="horizontal-form-item" valuePropName="checked">
                <Switch size="small" />
            </Form.Item>

            <Form.Item name="readOnly" label={t("Read Only")} className="horizontal-form-item" valuePropName="checked">
                <Switch size="small" />
            </Form.Item>

            <Form.Item name="hideInitially" label={t("Hide Initially")} className="horizontal-form-item" valuePropName="checked">
                <Switch size="small" />
            </Form.Item>

            <Form.Item name="questionCount" label={t("Number of questions")} initialValue={DEFAULT_QUESTIONS.length}>
                <InputNumber min={1} max={10} style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item name="optionCount" label={t("Number of options")} initialValue={DEFAULT_OPTIONS.length}>
                <InputNumber min={1} max={10} style={{ width: "100%" }} />
            </Form.Item>

            <div className="text-regular text-color-primary" style={{ marginBottom: 8 }}>
                {t("Questions")}
            </div>
            {Array.from({ length: questionCount }).map((_, index) => {
                const question = getItem(formValues?.questions, index, DEFAULT_QUESTIONS, "Question");
                return (
                    <Flex key={`question-${index}`} gap={8} align="baseline">
                        <Form.Item
                            name={["questions", index, "label"]}
                            initialValue={question.label}
                            style={{ width: "100%" }}
                            rules={[{ required: true, whitespace: true, message: t("Question is required") }]}
                        >
                            <Input placeholder={t("Enter question")} />
                        </Form.Item>
                        <Form.Item name={["questions", index, "value"]} initialValue={question.value} hidden>
                            <Input />
                        </Form.Item>
                        <Button
                            danger
                            type="text"
                            icon={<DeleteOutlined />}
                            disabled={questionCount <= 1}
                            onClick={() => removeQuestion(index)}
                        />
                    </Flex>
                );
            })}
            <Form.Item>
                <Button className="success-button" icon={<PlusOutlined />} onClick={addQuestion}>
                    {t("Add Question")}
                </Button>
            </Form.Item>

            <div className="text-regular text-color-primary" style={{ marginBottom: 8 }}>
                {t("Options and scores")}
            </div>
            {Array.from({ length: optionCount }).map((_, index) => {
                const option = getItem(formValues?.options, index, DEFAULT_OPTIONS, "Option", true);
                return (
                    <Flex key={`option-${index}`} gap={8} align="baseline">
                        <Form.Item
                            name={["options", index, "label"]}
                            initialValue={option.label}
                            style={{ flex: 1 }}
                            rules={[{ required: true, whitespace: true, message: t("Option is required") }]}
                        >
                            <Input placeholder={t("Enter option")} />
                        </Form.Item>
                        <Form.Item name={["options", index, "score"]} initialValue={option.score} style={{ width: 88 }}>
                            <InputNumber placeholder={t("Score")} style={{ width: "100%" }} />
                        </Form.Item>
                        <Form.Item name={["options", index, "value"]} initialValue={option.value} hidden>
                            <Input />
                        </Form.Item>
                        <Button
                            danger
                            type="text"
                            icon={<DeleteOutlined />}
                            disabled={optionCount <= 1}
                            onClick={() => removeOption(index)}
                        />
                    </Flex>
                );
            })}
            <Form.Item>
                <Button className="success-button" icon={<PlusOutlined />} onClick={addOption}>
                    {t("Add Option")}
                </Button>
            </Form.Item>
        </>
    );
};

export default RatingScaleForm;
